import { useEffect, useState } from "react";
import { apiClient, useAuth } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Megaphone, X, AlertTriangle, Send, Plus } from "lucide-react";
import { toast } from "sonner";

/**
 * AnnouncementsBanner — appears at the top of Dashboard for all users.
 * - Owners/managers see a "+ Post announcement" button and can compose.
 * - Employees see the latest non-dismissed announcements as a stack of
 *   dismissible cards. Urgent ones get a red left border.
 */
export const AnnouncementsBanner = () => {
  const { user, tenant } = useAuth();
  const canPost = (user?.role === "owner" || user?.role === "manager");
  const isFree = tenant?.plan === "free" || !tenant?.plan;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");
  const [notify, setNotify] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const res = await apiClient.get("/announcements/mine");
      setItems(res.data || []);
    } catch {
      // silent — banner is nice-to-have
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const dismiss = async (id) => {
    setItems((prev) => prev.filter((a) => a.announcement_id !== id));
    try {
      await apiClient.post(`/announcements/${id}/dismiss`);
    } catch {
      /* dismissal is best-effort — banner already gone locally */
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      toast.error("Title and body are required.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiClient.post("/announcements", {
        title: title.trim(),
        body: body.trim(),
        priority,
        notify,
      });
      const counts = res.data?.notify_counts;
      const parts = [];
      if (counts?.sms_sent) parts.push(`${counts.sms_sent} SMS`);
      if (counts?.email_sent) parts.push(`${counts.email_sent} email${counts.email_sent === 1 ? "" : "s"}`);
      toast.success(
        parts.length
          ? `Announcement posted — notified ${parts.join(" + ")}.`
          : "Announcement posted. Employees will see it next time they open Nirvanta."
      );
      setTitle(""); setBody(""); setPriority("normal"); setNotify(true);
      setComposing(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post announcement.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;
  if (!items.length && !canPost) return null;

  return (
    <div className="space-y-2" data-testid="announcements-banner">
      {items.map((a) => (
        <Card
          key={a.announcement_id}
          data-testid={`announcement-${a.announcement_id}`}
          className={`border-l-4 ${a.priority === "urgent" ? "border-l-red-500" : "border-l-[#0D9488]"} bg-white`}
        >
          <CardContent className="p-4 flex items-start gap-3">
            {a.priority === "urgent"
              ? <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              : <Megaphone className="w-5 h-5 text-[#0D9488] shrink-0 mt-0.5" />}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[#0A192F]">{a.title}</p>
              <p className="text-sm text-slate-600 whitespace-pre-wrap mt-0.5">{a.body}</p>
              <p className="text-[11px] text-slate-400 mt-1.5">
                Posted by {a.posted_by_name || "your manager"} · {new Date(a.created_at).toLocaleString()}
              </p>
            </div>
            <button
              type="button"
              onClick={() => dismiss(a.announcement_id)}
              className="text-slate-400 hover:text-slate-700 shrink-0"
              aria-label="Dismiss"
              data-testid={`dismiss-${a.announcement_id}`}
            >
              <X className="w-4 h-4" />
            </button>
          </CardContent>
        </Card>
      ))}

      {canPost && (
        <div className="flex justify-end">
          {isFree ? (
            <Button
              onClick={() => window.location.assign("/settings?tab=subscription")}
              data-testid="new-announcement-upgrade-btn"
              className="bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 font-semibold shadow-sm"
              title="Announcements are a Pro feature"
            >
              <Megaphone className="w-4 h-4 mr-2" /> Post announcement — <span className="ml-1 text-xs uppercase tracking-wide">Pro</span>
            </Button>
          ) : (
            <Button
              onClick={() => setComposing(true)}
              data-testid="new-announcement-btn"
              className="bg-[#0D9488] hover:bg-[#14B8A6] text-white font-semibold shadow-sm"
            >
              <Megaphone className="w-4 h-4 mr-2" /> Post announcement
            </Button>
          )}
        </div>
      )}
      <Dialog open={composing} onOpenChange={setComposing}>
        <DialogContent className="sm:max-w-lg" data-testid="new-announcement-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-[#0D9488]" /> New announcement
            </DialogTitle>
            <DialogDescription>
              Sends to everyone active at your company — SMS to phone-only staff, email to the rest.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="ann-title" className="block text-xs font-medium text-slate-600">Title</label>
              <Input
                id="ann-title"
                placeholder="Store closed Sunday"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
                disabled={submitting}
                data-testid="ann-title-input"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="ann-body" className="block text-xs font-medium text-slate-600">Message</label>
              <Textarea
                id="ann-body"
                placeholder="Give your team the details they need to know."
                rows={5}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={4000}
                disabled={submitting}
                data-testid="ann-body-input"
              />
              <p className="text-[10px] text-slate-400 text-right">{body.length} / 4000</p>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="priority"
                  checked={priority === "normal"}
                  onChange={() => setPriority("normal")}
                  className="accent-[#0D9488]"
                  data-testid="ann-priority-normal"
                />
                Normal
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="priority"
                  checked={priority === "urgent"}
                  onChange={() => setPriority("urgent")}
                  className="accent-red-500"
                  data-testid="ann-priority-urgent"
                />
                Urgent (red banner)
              </label>
              <label className="flex items-center gap-2 cursor-pointer ml-auto">
                <input
                  type="checkbox"
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                  className="accent-[#0D9488]"
                  data-testid="ann-notify-input"
                />
                Notify team by SMS/email now
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setComposing(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting || !title.trim() || !body.trim()}
                data-testid="ann-submit-btn"
                className="bg-[#0D9488] hover:bg-[#14B8A6]"
              >
                <Send className="w-4 h-4 mr-2" />
                {submitting ? "Posting…" : "Post"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
